import React, { useState } from 'react';
import { Modal, Button, Typography, Alert, Progress, List, Space, Tag, Divider, Table, Upload, message, Collapse } from 'antd';
import { UploadOutlined, DownloadOutlined, CheckCircleTwoTone, CloseCircleTwoTone, WarningTwoTone, InfoCircleOutlined } from '@ant-design/icons';
import { useMutation } from 'react-query';
import api from '../config/api';

interface BulkUploadDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  templateUrl: string;
  uploadEndpoint: string;
  onSuccess?: () => void;
  entityType: 'businesses' | 'users';
}

interface UploadResult {
  success: number;
  skipped: number;
  duplicates: number;
  errors: number;
  total: number;
  reportUrl: string;
  summary?: {
    uploaded: Array<any>;
    skipped: Array<any>;
    duplicates: Array<any>;
    errors: Array<any>;
  };
}

interface ValidationResult {
  valid: boolean;
  totalRows: number;
  errors: Array<{
    row: number;
    field: string;
    error: string;
  }>;
  warnings: Array<{
    row: number;
    field: string;
    warning: string;
  }>;
  duplicatesInCSV: Array<{
    row: number;
    previousRow: number;
    field: string;
    value: string;
    error: string;
  }>;
  duplicatesInDB: Array<{
    row: number;
    field: string;
    value: string;
    existingBusiness?: any;
    error: string;
  }>;
}

const BulkUploadDialog: React.FC<BulkUploadDialogProps> = ({
  open,
  onClose,
  title,
  templateUrl,
  uploadEndpoint,
  onSuccess,
  entityType
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Validation mutation
  const validateMutation = useMutation(
    async (formData: FormData) => {
      const validateEndpoint = entityType === 'businesses' 
        ? '/api/businesses/validate-bulk' 
        : '/api/users/validate-bulk';
      
      const response = await api.post(validateEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    {
      onSuccess: (data: ValidationResult) => {
        setValidationResult(data);
        setIsValidating(false);
        
        if (data.valid) {
          if (data.warnings.length > 0) {
            message.warning(`Validation passed with ${data.warnings.length} warnings`);
          } else {
            message.success(`Validation passed! ${data.totalRows} rows ready to upload`);
          }
          setShowConfirmation(true);
        } else {
          const totalErrors = data.errors.length + data.duplicatesInCSV.length + data.duplicatesInDB.length;
          message.error(`Validation failed with ${totalErrors} errors`);
        }
      },
      onError: (error: any) => {
        setIsValidating(false);
        message.error(error.response?.data?.error || 'Validation failed');
      }
    }
  );

  // Upload mutation
  const uploadMutation = useMutation(
    async (formData: FormData) => {
      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      try {
        const response = await api.post(uploadEndpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        clearInterval(interval);
        setUploadProgress(100);
        return response.data;
      } catch (error) {
        clearInterval(interval);
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        setUploadResult(data);
        setShowConfirmation(false);
        
        const successMsg = [];
        if (data.success > 0) successMsg.push(`${data.success} uploaded`);
        if (data.skipped > 0) successMsg.push(`${data.skipped} skipped (already in database)`);
        if (data.duplicates > 0) successMsg.push(`${data.duplicates} duplicates in CSV`);
        if (data.errors > 0) successMsg.push(`${data.errors} errors`);
        
        if (data.success > 0) {
          message.success(`Upload complete: ${successMsg.join(', ')}`);
        } else {
          message.warning(`Upload complete: ${successMsg.join(', ')}`);
        }
        
        onSuccess?.();
      },
      onError: (error: any) => {
        setUploadProgress(0);
        message.error(error.response?.data?.error || 'Upload failed');
      }
    }
  );

  const handleFileChange = (selectedFile: File) => {
    setFile(selectedFile);
    setUploadResult(null);
    setValidationResult(null);
    setShowConfirmation(false);
    setUploadProgress(0);
    
    // Automatically validate the file
    setIsValidating(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    validateMutation.mutate(formData);
  };

  const handleConfirmUpload = () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    uploadMutation.mutate(formData);
  };

  const handleDownloadTemplate = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    const link = document.createElement('a');
    link.href = templateUrl;
    link.download = `${entityType}_template.csv`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    setFile(null);
    setUploadResult(null);
    setValidationResult(null);
    setShowConfirmation(false);
    setIsValidating(false);
    setUploadProgress(0);
    onClose();
  };

  const totalErrors = validationResult 
    ? validationResult.errors.length + validationResult.duplicatesInCSV.length + validationResult.duplicatesInDB.length 
    : 0;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleClose}
      width={900}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={uploadMutation.isLoading}>
          {uploadResult ? 'Close' : 'Cancel'}
        </Button>,
        showConfirmation && (
          <Button 
            key="confirm" 
            type="primary" 
            onClick={handleConfirmUpload} 
            disabled={uploadMutation.isLoading}
            loading={uploadMutation.isLoading}
          >
            {uploadMutation.isLoading ? 'Uploading...' : 'Confirm & Upload'}
          </Button>
        )
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert type="info" showIcon message={
          <span>Download the template file, fill it out with your data, and upload it here. The file will be validated before upload.</span>
        } />

        <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>Download Template</Button>

        <Divider />

        <div>
          <Typography.Title level={5} style={{ marginTop: 0 }}>Upload CSV File</Typography.Title>
          <Upload
            beforeUpload={(f) => { 
              handleFileChange(f as File);
              return false; 
            }}
            accept=".csv"
            maxCount={1}
            showUploadList={file ? { showRemoveIcon: true } : false}
            onRemove={() => { 
              setFile(null); 
              setValidationResult(null); 
              setShowConfirmation(false);
              setUploadProgress(0);
            }}
            disabled={isValidating || uploadMutation.isLoading}
          >
            <Button 
              icon={<UploadOutlined />} 
              disabled={isValidating || uploadMutation.isLoading}
            >
              Choose CSV File
            </Button>
          </Upload>
          {file && (
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Selected: {file.name}
            </Typography.Text>
          )}
        </div>

        {isValidating && (
          <div>
            <Typography.Text><InfoCircleOutlined /> Validating CSV file...</Typography.Text>
            <Progress percent={50} status="active" showInfo={false} style={{ marginTop: 8 }} />
          </div>
        )}

        {validationResult && (
          <div>
            <Typography.Title level={5}>Validation Results</Typography.Title>
            
            {/* Summary Cards */}
            <Space size="small" style={{ marginBottom: 16 }}>
              <Tag icon={<InfoCircleOutlined />} color="blue">{validationResult.totalRows} total rows</Tag>
              {totalErrors > 0 && (
                <Tag icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />} color="error">{totalErrors} errors</Tag>
              )}
              {validationResult.warnings.length > 0 && (
                <Tag icon={<WarningTwoTone twoToneColor="#faad14" />} color="warning">{validationResult.warnings.length} warnings</Tag>
              )}
              {validationResult.valid && totalErrors === 0 && (
                <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">Ready to upload</Tag>
              )}
            </Space>

            {/* Errors Section */}
            {totalErrors > 0 && (
              <Alert
                type="error"
                showIcon
                message={`${totalErrors} Errors Found - Upload Blocked`}
                description="Fix these errors in your CSV file and try again."
                style={{ marginBottom: 12 }}
              />
            )}

            <Collapse 
              bordered={false}
              style={{ background: '#f5f5f5' }}
              items={[
                // Field Validation Errors
                validationResult.errors.length > 0 && {
                  key: 'errors',
                  label: (
                    <span>
                      <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ marginRight: 8 }} />
                      Field Validation Errors ({validationResult.errors.length})
                    </span>
                  ),
                  children: (
                    <List
                      size="small"
                      dataSource={validationResult.errors}
                      renderItem={(err) => (
                        <List.Item>
                          <Typography.Text>
                            Row {err.row} - {err.field}: <Typography.Text type="danger">{err.error}</Typography.Text>
                          </Typography.Text>
                        </List.Item>
                      )}
                    />
                  ),
                },
                // Duplicates in CSV
                validationResult.duplicatesInCSV.length > 0 && {
                  key: 'duplicates-csv',
                  label: (
                    <span>
                      <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ marginRight: 8 }} />
                      Duplicates Within CSV ({validationResult.duplicatesInCSV.length})
                    </span>
                  ),
                  children: (
                    <List
                      size="small"
                      dataSource={validationResult.duplicatesInCSV}
                      renderItem={(dup) => (
                        <List.Item>
                          <Typography.Text>
                            Row {dup.row} - {dup.field}: <Typography.Text type="danger">"{dup.value}"</Typography.Text> already exists at row {dup.previousRow}
                          </Typography.Text>
                        </List.Item>
                      )}
                    />
                  ),
                },
                // Duplicates in Database
                validationResult.duplicatesInDB.length > 0 && {
                  key: 'duplicates-db',
                  label: (
                    <span>
                      <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ marginRight: 8 }} />
                      Already Exists in Database ({validationResult.duplicatesInDB.length})
                    </span>
                  ),
                  children: (
                    <List
                      size="small"
                      dataSource={validationResult.duplicatesInDB}
                      renderItem={(dup) => (
                        <List.Item>
                          <Typography.Text>
                            Row {dup.row} - {dup.field}: <Typography.Text type="danger">"{dup.value}"</Typography.Text>
                            {dup.existingBusiness && ` - ${dup.existingBusiness.business_name}`}
                          </Typography.Text>
                        </List.Item>
                      )}
                    />
                  ),
                },
                // Warnings
                validationResult.warnings.length > 0 && {
                  key: 'warnings',
                  label: (
                    <span>
                      <WarningTwoTone twoToneColor="#faad14" style={{ marginRight: 8 }} />
                      Warnings ({validationResult.warnings.length})
                    </span>
                  ),
                  children: (
                    <List
                      size="small"
                      dataSource={validationResult.warnings}
                      renderItem={(warn) => (
                        <List.Item>
                          <Typography.Text>
                            Row {warn.row} - {warn.field}: <Typography.Text type="warning">{warn.warning}</Typography.Text>
                          </Typography.Text>
                        </List.Item>
                      )}
                    />
                  ),
                },
              ].filter(Boolean) as any[]}
            />

            {validationResult.valid && totalErrors === 0 && (
              <Alert
                type="success"
                showIcon
                message="Validation Passed!"
                description={`${validationResult.totalRows} rows are ready to be uploaded. Click "Confirm & Upload" to proceed.`}
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        )}

        {uploadMutation.isLoading && (
          <div>
            <Typography.Text><InfoCircleOutlined /> Uploading and processing...</Typography.Text>
            <Progress percent={uploadProgress} status="active" style={{ marginTop: 8 }} />
          </div>
        )}

        {uploadResult && (
          <div>
            <Divider />
            <Typography.Title level={5}>Upload Complete</Typography.Title>
            
            {/* Summary Tags */}
            <Space size="small" style={{ marginBottom: 16 }} wrap>
              <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">
                {uploadResult.success} uploaded
              </Tag>
              {uploadResult.skipped > 0 && (
                <Tag icon={<InfoCircleOutlined />} color="blue">
                  {uploadResult.skipped} skipped (already in DB)
                </Tag>
              )}
              {uploadResult.duplicates > 0 && (
                <Tag icon={<WarningTwoTone twoToneColor="#faad14" />} color="warning">
                  {uploadResult.duplicates} duplicates in CSV
                </Tag>
              )}
              {uploadResult.errors > 0 && (
                <Tag icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />} color="error">
                  {uploadResult.errors} errors
                </Tag>
              )}
              <Tag color="default">
                {uploadResult.total} total rows processed
              </Tag>
            </Space>

            {/* Download Report Button */}
            <Alert
              type="info"
              showIcon
              message="Detailed Report Available"
              description={
                <Space direction="vertical" size="small">
                  <Typography.Text>
                    A detailed CSV report has been generated showing which businesses were uploaded, skipped, or had errors.
                  </Typography.Text>
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      window.open(uploadResult.reportUrl, '_blank');
                    }}
                  >
                    Download Detailed Report (CSV)
                  </Button>
                </Space>
              }
              style={{ marginBottom: 16 }}
            />

            {/* Quick Summary in Collapsible */}
            {uploadResult.summary && (
              <Collapse 
                bordered={false}
                style={{ background: '#f5f5f5' }}
                items={[
                  uploadResult.summary.uploaded.length > 0 && {
                    key: 'uploaded',
                    label: (
                      <span>
                        <CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 8 }} />
                        Uploaded ({uploadResult.summary.uploaded.length})
                      </span>
                    ),
                    children: (
                      <List
                        size="small"
                        dataSource={uploadResult.summary.uploaded.slice(0, 10)}
                        renderItem={(item: any) => (
                          <List.Item>
                            <Typography.Text>
                              Row {item.row}: {item.business_name} - {item.mid}
                            </Typography.Text>
                          </List.Item>
                        )}
                        footer={uploadResult.summary.uploaded.length > 10 && (
                          <Typography.Text type="secondary">
                            ... and {uploadResult.summary.uploaded.length - 10} more. Download the report for full details.
                          </Typography.Text>
                        )}
                      />
                    ),
                  },
                  uploadResult.summary.skipped.length > 0 && {
                    key: 'skipped',
                    label: (
                      <span>
                        <InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        Skipped - Already in Database ({uploadResult.summary.skipped.length})
                      </span>
                    ),
                    children: (
                      <List
                        size="small"
                        dataSource={uploadResult.summary.skipped.slice(0, 10)}
                        renderItem={(item: any) => (
                          <List.Item>
                            <Typography.Text>
                              Row {item.row}: {item.business_name} - {item.mid} - {item.reason}
                            </Typography.Text>
                          </List.Item>
                        )}
                        footer={uploadResult.summary.skipped.length > 10 && (
                          <Typography.Text type="secondary">
                            ... and {uploadResult.summary.skipped.length - 10} more. Download the report for full details.
                          </Typography.Text>
                        )}
                      />
                    ),
                  },
                  uploadResult.summary.duplicates.length > 0 && {
                    key: 'duplicates',
                    label: (
                      <span>
                        <WarningTwoTone twoToneColor="#faad14" style={{ marginRight: 8 }} />
                        Duplicates in CSV ({uploadResult.summary.duplicates.length})
                      </span>
                    ),
                    children: (
                      <List
                        size="small"
                        dataSource={uploadResult.summary.duplicates.slice(0, 10)}
                        renderItem={(item: any) => (
                          <List.Item>
                            <Typography.Text type="warning">
                              Row {item.row}: {item.business_name} - {item.mid} - {item.reason}
                            </Typography.Text>
                          </List.Item>
                        )}
                        footer={uploadResult.summary.duplicates.length > 10 && (
                          <Typography.Text type="secondary">
                            ... and {uploadResult.summary.duplicates.length - 10} more. Download the report for full details.
                          </Typography.Text>
                        )}
                      />
                    ),
                  },
                  uploadResult.summary.errors.length > 0 && {
                    key: 'errors',
                    label: (
                      <span>
                        <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ marginRight: 8 }} />
                        Errors ({uploadResult.summary.errors.length})
                      </span>
                    ),
                    children: (
                      <List
                        size="small"
                        dataSource={uploadResult.summary.errors.slice(0, 10)}
                        renderItem={(item: any) => (
                          <List.Item>
                            <Typography.Text type="danger">
                              Row {item.row}: {item.business_name} - {item.reason}
                            </Typography.Text>
                          </List.Item>
                        )}
                        footer={uploadResult.summary.errors.length > 10 && (
                          <Typography.Text type="secondary">
                            ... and {uploadResult.summary.errors.length - 10} more. Download the report for full details.
                          </Typography.Text>
                        )}
                      />
                    ),
                  },
                ].filter(Boolean) as any[]}
              />
            )}
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default BulkUploadDialog;
